import { Field, Input, Spinner, Text } from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useController, Control, FieldValues } from "react-hook-form";
import { InputGroup } from "../ui/input-group";
import { AppColors } from "@/styles/colors";
import { onlyNumbers } from "@/utils/reg-expressions";
import { isEmpty } from "@/utils/query-string";
import { existePersona } from "@/services/persons.service";
import { Check, X } from "lucide-react";
import { useDniValidationStore } from "@/store/dniValidationStore";

type DebouncedInputProps = {
  name: string;
  control: Control<FieldValues>;
  debounce?: number;
  placeholder?: string;
  height?: string;
  minHeight?: string;
  required?: boolean;
  maxLength?: number;
  labelText?: string;
  disabled?: boolean;
  validate?: boolean;
};

export const DniSearchInput = ({
  name,
  control,
  debounce = 1000,
  placeholder,
  height,
  maxLength,
  minHeight,
  required,
  labelText,
  disabled,
  validate,
}: DebouncedInputProps) => {
  const {
    field: { onChange, value },
  } = useController({ name, control });

  const [error, setError] = useState<{ message: string } | null>(null);

  const [localValue, setLocalValue] = useState(value ?? "");

  const [loadingQuery, setLoadingQuery] = useState<boolean>(false);

  const [queryExec, setQueryExec] = useState<boolean>(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { setValidation } = useDniValidationStore();

  useEffect(() => {
    setLocalValue(value ?? "");
  }, [value]);

  useEffect(() => {
    setError(null);
    setQueryExec(false);
  }, [localValue]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      if (!validate) return;
      setError(null);
      onChange(localValue);

      if (isEmpty(value)) return;
      if (onlyNumbers.test(localValue)) {
        setLoadingQuery(true);
        const existe = await existePersona({ dni: localValue });

        if (existe) {
          setError({
            message: "La persona ya existe.",
          });
          setValidation({
            error: "La persona ya existe",
            isValid: false,
          });
        } else {
          setValidation({
            error: null,
            isValid: true,
          });
          setError(null);
          setQueryExec(true);
        }
        setLoadingQuery(false);
      } else {
        setError({
          message: "Debe ser un DNI válido",
        });
        setValidation({
          error: "Debe ser un DNI válido",
          isValid: false,
        });
      }
    }, debounce);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [localValue]);

  const endElementMemo = useMemo(() => {
    if (!validate) return null;
    if (loadingQuery)
      return (
        <Spinner marginRight="8px" data-testid="dni-validation-spinner-icon" />
      );
    if (error?.message)
      return (
        <X
          color={AppColors.RED_ICON}
          style={{ marginRight: "8px" }}
          data-testid="dni-validation-error-icon"
        />
      );
    if (!error && queryExec)
      return (
        <Check
          color={AppColors.GREEN_ICON}
          style={{ marginRight: "8px" }}
          data-testid="dni-validation-passed-icon"
        />
      );
  }, [error, loadingQuery, queryExec, validate]);

  return (
    <Field.Root invalid={!!error} required={required}>
      <Field.Label>
        <Text fontWeight={600}>{labelText}</Text>
        {required && (
          <Text fontWeight={600} color={AppColors.REQUIRED}>
            *
          </Text>
        )}
      </Field.Label>
      <InputGroup
        width="100%"
        flex="1"
        _placeholder={{
          color: AppColors.LIGHT_GRAY_TEXT,
        }}
        endElement={endElementMemo}
      >
        <Input
          value={value}
          placeholder={placeholder}
          height={height}
          minH={minHeight}
          fontSize="14px"
          paddingX="12px"
          disabled={disabled}
          maxLength={maxLength}
          onChange={onChange}
          data-testid="dni-validation-input"
        />
      </InputGroup>
      {error && <Field.ErrorText>{String(error?.message)}</Field.ErrorText>}
    </Field.Root>
  );
};
